import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Upload, Paperclip, X, FileText, Image as ImageIcon, DollarSign, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = [
  "Open",
  "Awaiting Seller Response",
  "Awaiting Buyer Response",
  "Under Investigation",
  "Replacement Shipped",
  "Refunded",
  "Closed - Resolved",
  "Closed - Denied",
];
const CLOSED = new Set(["Refunded", "Closed - Resolved", "Closed - Denied"]);
const TYPE_LABEL = { return: "Return Request", cancellation: "Cancellation Request", inr: "Item Not Received" };

function AttachmentChip({ att, onRemove }) {
  const isImg = (att.content_type || "").startsWith("image/");
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noreferrer"
      onClick={onRemove ? (e) => e.preventDefault() : undefined}
      className="flex items-center gap-2 px-2 py-1 border border-[#E5E1D8] bg-white"
    >
      {isImg ? <ImageIcon size={13} /> : <FileText size={13} />}
      <span className="font-mono text-[10px] uppercase tracking-editorial truncate max-w-[160px]">{att.filename}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} className="text-[#7A2021]" aria-label="Remove">
          <X size={11} />
        </button>
      )}
    </a>
  );
}

function MsgBubble({ msg }) {
  const isBuyer = msg.author_type === "buyer";
  return (
    <div className={`flex ${isBuyer ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[80%] p-3 border ${isBuyer ? "border-[#1A1714] bg-white" : "border-[#7A2021] bg-[#F9F6F0]"}`}>
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-1">
          {isBuyer ? `Buyer · ${msg.author_name}` : `You (${msg.author_name})`} · {new Date(msg.created_at).toLocaleString()}
        </p>
        {msg.body && <p className="font-serif text-sm text-[#1A1714] whitespace-pre-wrap break-words">{msg.body}</p>}
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {msg.attachments.map((a) => <AttachmentChip key={a.path} att={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminCaseDetailDialog({ caseId, open, onOpenChange, onChanged }) {
  const [caseData, setCaseData] = useState(null);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refunding, setRefunding] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!open || !caseId) return;
    api.get(`/admin/cases/${caseId}`)
      .then((r) => setCaseData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load case"));
  };

  useEffect(() => {
    if (open && caseId) {
      setReply(""); setAttachments([]); setRefundAmount(""); setRefundNote("");
      load();
    }
    // eslint-disable-next-line
  }, [open, caseId]);

  const uploadOne = async (f) => {
    if (attachments.length >= 5) return toast.error("Max 5 attachments");
    if (f.size > 5 * 1024 * 1024) return toast.error(`${f.name} > 5MB`);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cases/upload-attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const onFiles = async (e) => {
    for (const f of Array.from(e.target.files || [])) { await uploadOne(f); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() && !attachments.length) return toast.error("Message or attachment required");
    setSending(true);
    try {
      const { data } = await api.post(`/admin/cases/${caseId}/messages`, { body: reply.trim(), attachments });
      setCaseData(data);
      setReply(""); setAttachments([]);
      toast.success("Reply sent to buyer");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!newStatus || newStatus === caseData.status) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/cases/${caseId}/status`, { status: newStatus });
      setCaseData(data);
      toast.success(`Status → ${newStatus}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Status update failed"); }
    finally { setSaving(false); }
  };

  const doRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a positive refund amount");
    if (!window.confirm(`Refund $${amt.toFixed(2)} to the buyer via Stripe? This is irreversible.`)) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/cases/${caseId}/refund`, { amount: amt, note: refundNote.trim() || null });
      setCaseData(data);
      setRefundAmount(""); setRefundNote("");
      toast.success(`Refunded $${amt.toFixed(2)}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefunding(false); }
  };

  if (!caseData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="bg-[#F9F6F0] max-w-4xl"
          data-testid="admin-case-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Loading case…</DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              Fetching case details.
            </DialogDescription>
          </DialogHeader>
          <p className="p-4 font-serif italic text-lg">Please wait…</p>
        </DialogContent>
      </Dialog>
    );
  }

  const order = caseData.order_snapshot;
  const orderAmount = Number(caseData.order_amount || order?.amount || 0);
  const totalRefunded = Number(caseData.total_refunded || 0);
  const remaining = Math.max(0, orderAmount - totalRefunded);
  const closed = CLOSED.has(caseData.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto p-0"
        data-testid="admin-case-detail-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl">Case #{caseData.case_number}</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            {TYPE_LABEL[caseData.type]} · Order #{caseData.order_id} · Opened {new Date(caseData.created_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Left: thread */}
          <div className="lg:col-span-2 space-y-4">
            <div className="border border-[#E5E1D8] p-4 bg-white space-y-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Reason</p>
                <p className="font-serif text-lg" data-testid="admin-case-reason">{caseData.reason}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Buyer</p>
                  <p className="font-mono">{caseData.user_name}</p>
                  <p className="font-mono text-[#1A1714]/60">{caseData.user_email}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Order amount</p>
                  <p className="font-serif text-xl">${orderAmount.toFixed(2)}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Refunded</p>
                  <p className="font-serif text-xl text-[#7A2021]" data-testid="admin-case-refunded">${totalRefunded.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3" data-testid="admin-case-thread">
              {(caseData.messages || []).map((m) => <MsgBubble key={m.id} msg={m} />)}
            </div>

            {!closed && (
              <form onSubmit={sendReply} className="border border-[#E5E1D8] p-4 bg-white" data-testid="admin-case-reply-form">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply to the buyer…"
                  className="rounded-none border-[#E5E1D8] min-h-24"
                  data-testid="admin-case-reply-input"
                />
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {attachments.map((a, i) => (
                      <AttachmentChip key={a.path} att={a} onRemove={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3">
                  <input ref={fileRef} type="file" multiple className="hidden" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" onChange={onFiles} data-testid="admin-case-reply-file-input" />
                  <Button type="button" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="admin-case-reply-attach" className="rounded-none border-[#1A1714] font-mono text-[10px] uppercase tracking-editorial">
                    <Paperclip size={12} className="mr-2" /> {uploading ? "Uploading…" : "Attach"}
                  </Button>
                  <Button type="submit" disabled={sending} data-testid="admin-case-reply-send" className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial">
                    {sending ? "Sending…" : "Send to buyer"}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Right: actions */}
          <aside className="space-y-4">
            <div className="border border-[#E5E1D8] p-4 bg-white">
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">Case status</p>
              <select
                value={caseData.status}
                disabled={saving}
                onChange={(e) => changeStatus(e.target.value)}
                data-testid="admin-case-status-select"
                className="w-full h-11 border border-[#E5E1D8] rounded-none bg-white px-3 font-mono text-xs uppercase tracking-editorial"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
                Buyer is notified by email on every status change.
              </p>
            </div>

            <div className="border border-[#E5E1D8] p-4 bg-white">
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2 flex items-center gap-2">
                <DollarSign size={12} /> Refund via Stripe
              </p>
              <p className="font-serif text-2xl">${remaining.toFixed(2)}<span className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 ml-2">available</span></p>
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRefundAmount(remaining.toFixed(2))}
                    disabled={remaining <= 0}
                    data-testid="admin-case-refund-full-btn"
                    className="rounded-none border-[#1A1714] font-mono text-[10px] uppercase tracking-editorial flex-1"
                  >
                    Full (${remaining.toFixed(2)})
                  </Button>
                </div>
                <Input
                  type="number"
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="Custom amount"
                  disabled={remaining <= 0}
                  data-testid="admin-case-refund-amount"
                  className="rounded-none border-[#E5E1D8]"
                />
                <Textarea
                  value={refundNote}
                  onChange={(e) => setRefundNote(e.target.value)}
                  placeholder="Internal / buyer-visible note (optional)"
                  data-testid="admin-case-refund-note"
                  className="rounded-none border-[#E5E1D8] min-h-16 text-sm"
                />
                <Button
                  type="button"
                  onClick={doRefund}
                  disabled={refunding || remaining <= 0}
                  data-testid="admin-case-refund-submit"
                  className="w-full rounded-none bg-[#7A2021] text-[#F9F6F0] hover:bg-[#5A1618] font-mono text-xs uppercase tracking-editorial"
                >
                  {refunding ? "Refunding…" : "Refund now"}
                </Button>
              </div>
              {caseData.refunds && caseData.refunds.length > 0 && (
                <div className="mt-3 border-t border-[#E5E1D8] pt-3 space-y-1" data-testid="admin-case-refunds-history">
                  <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">History</p>
                  {caseData.refunds.map((r) => (
                    <p key={r.id} className="font-mono text-[10px] text-[#1A1714]">
                      ${Number(r.amount).toFixed(2)} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {order && (
              <div className="border border-[#E5E1D8] p-4 bg-white text-xs">
                <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">Order snapshot</p>
                <p className="font-mono">Status: <span className="text-[#7A2021]">{order.status}</span></p>
                {order.tracking_number && <p className="font-mono">Tracking: {order.tracking_number} ({order.carrier})</p>}
                {order.shipped_at && <p className="font-mono">Shipped: {new Date(order.shipped_at).toLocaleDateString()}</p>}
                <Link
                  to={`/track/${caseData.order_id}?email=${encodeURIComponent(caseData.user_email)}`}
                  target="_blank"
                  className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/70 hover:text-[#7A2021]"
                >
                  <ExternalLink size={11} /> View tracking
                </Link>
              </div>
            )}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
